import React, { useEffect, useMemo, useState } from "react";
import { TreeEvergreen, CaretDown, CaretUp, MapPin } from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

function fmtAcres(a) {
  if (a == null) return "—";
  if (a < 0.1) return "<0.1 ac";
  if (a < 10) return `${a.toFixed(1)} ac`;
  if (a < 1000) return `${a.toFixed(0)} ac`;
  return `${(a / 1000).toFixed(1)}k ac`;
}

export default function DnrFiresCard({ fires = [], onFlyTo, onHover, hoverId = null }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!hoverId || !fires.some((f) => f.id === hoverId)) return;
    setOpen(true);
    const t = setTimeout(() => {
      document
        .querySelector(`[data-testid="dnr-row-${hoverId}"]`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 150);
    return () => clearTimeout(t);
  }, [hoverId, fires]);

  const stats = useMemo(() => {
    const active = fires.filter((f) => f.is_active);
    const acres = active.reduce((s, f) => s + (f.acres || 0), 0);
    const biggest = active.reduce(
      (m, f) => ((f.acres || 0) > (m?.acres || 0) ? f : m),
      null,
    );
    return { active, totalAcres: acres, biggest, total: fires.length };
  }, [fires]);

  if (stats.total === 0) return null;

  // Active fires first, then by acreage descending
  const sorted = [...fires].sort((a, b) => {
    if (a.is_active !== b.is_active) return b.is_active ? 1 : -1;
    return (b.acres || 0) - (a.acres || 0);
  });

  return (
    <section
      data-testid="dnr-fires-card"
      className="border-b border-tac-border bg-tac-surface/40"
    >
      <button
        type="button"
        data-testid="dnr-card-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-tac-surface/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          <TreeEvergreen size={14} weight="fill" className="text-emerald-500" />
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-text">
            WA DNR · Fire Statistics
          </span>
          <span className="text-[15px] font-mono-tac tabular text-tac-faint">
            · {stats.active.length} active
          </span>
        </div>
        {open ? (
          <CaretUp size={12} weight="bold" className="text-tac-dim" />
        ) : (
          <CaretDown size={12} weight="bold" className="text-tac-dim" />
        )}
      </button>

      {open && (
        <>
          <div className="px-4 pb-2 flex items-center gap-3 text-[10px] font-mono-tac tabular flex-wrap">
            <span className="text-tac-dim">{stats.total} total · 14d</span>
            <span className="text-tac-dim">
              {fmtAcres(stats.totalAcres)} on DNR lands
            </span>
            {stats.biggest && (
              <span className="text-tac-faint ml-auto truncate max-w-[160px]">
                largest: {stats.biggest.name}
              </span>
            )}
          </div>

          <ul className="divide-y divide-tac-border/50" data-testid="dnr-list">
            {(() => {
              const visible = sorted.slice(0, 6);
              if (hoverId && !visible.some((f) => f.id === hoverId)) {
                const extra = sorted.find((f) => f.id === hoverId);
                if (extra) visible.push(extra);
              }
              return visible;
            })().map((f) => (
              <li
                key={f.id}
                data-testid={`dnr-row-${f.id}`}
                className={`px-4 py-2 border-l-2 transition-colors ${
                  f.id === hoverId ? "bg-tac-surface2 border-l-tac-primary" : "border-l-transparent"
                }`}
              >
                <button
                  type="button"
                  onClick={() => onFlyTo?.(f.latitude, f.longitude)}
                  onMouseEnter={() => onHover?.(f.id)}
                  onMouseLeave={() => onHover?.(null)}
                  className="w-full text-left group"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          f.is_active ? "bg-tac-danger" : "bg-tac-faint"
                        }`}
                      />
                      <span className="font-mono-tac text-[12px] text-tac-text truncate group-hover:text-tac-primary transition-colors">
                        {f.name}
                      </span>
                    </div>
                    <span className="text-[10px] font-mono-tac tabular text-tac-faint shrink-0">
                      {fmtAcres(f.acres)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] font-mono-tac text-tac-dim pl-4 mt-0.5">
                    <MapPin size={9} weight="fill" />
                    <span className="truncate">
                      {f.county || "Unknown"} · {f.cause || "Cause unknown"}
                    </span>
                    <span className="ml-auto text-tac-faint shrink-0">
                      {fmtDate(f.discovered_at)}
                    </span>
                  </div>
                </button>
              </li>
            ))}
            {sorted.length > 6 && (
              <li className="px-4 py-1.5 text-[10px] font-mono-tac text-tac-faint text-center">
                + {sorted.length - 6} more on map
              </li>
            )}
          </ul>
        </>
      )}
      <div className="px-4 py-1.5 text-[10px] font-mono-tac text-tac-faint border-t border-tac-border/40">
        Source: WA DNR Wildland Fire Mgmt · gis.dnr.wa.gov
      </div>
    </section>
  );
}
